import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';

import { SignupSessionError } from '@/auth';
import { SignupCredentialsForm } from '@/auth/components/SMBSignupModal/SignupCredentialsForm';
import type { SMBSignupFormData } from '@/auth/components/SMBSignupModal/types';
import SocialSignInButtons, {
  isSocialSignInEnabled,
} from '@/auth/components/SocialSignInButtons/SocialSignInButtons';
import { appendSignupParams } from '@/auth/utils/signupParams';
import { socialProviderName } from '@/auth/utils/socialSignIn';
import { SOCIAL_SIGNUP_ENDED, socialErrorMessage } from '@/auth/utils/socialSignInFailure';
import Alert from '@/components/Alert';
import Button from '@/componentsv2/Button';
import { ERROR_CODES, firstErrorCode } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';

import { STEP_COPY } from '../constants';
import { useAccountStepSocial } from '../hooks/useAccountStepSocial';
import type { useSelfServiceSMBSignupFlow } from '../hooks/useSelfServiceSMBSignupFlow';
import type { AccountData, SignupOutcome } from '../types';
import { accountStepFieldErrors } from '../utils/accountStepErrors';
import { type PostVerifyLookupDeps, runPostVerifyLookups } from '../utils/postVerifyLookups';
import { endOnSignInRequired } from '../utils/signInRequired';

function errorHasCode(err: unknown, code: string): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    (err as { code?: unknown }).code === code
  );
}

// Step A — account creation. After ENG-5201's 2026-05-08 phoneless pivot,
// this step delegates the form body to the shared SignupCredentialsForm
// (extracted from SMBSignupForm) so the visual is pixel-identical to the
// legacy "Get Free Access" gate. Outer chrome (Box padding, header
// Typography, subtitle) mirrors SMBSignupModal's `<Box sx={{ p: 3 }}>`
// content area.
//
// On success: handleSubmit calls onSignup (createUser w/ signup_subtype:
// "self_service" + authSession.authenticate), then dispatches
// flow.accountCreated to advance the reducer to the email-otp step. A social
// signup whose email the api stored as verified (ENG-6166) skips that step:
// the post-verify lookups run here and flow.accountVerified routes on them.
// Email-already-exists errors are mapped to the email field via React
// Final Form ValidationErrors so they surface inline (matches SMBSignupForm
// behavior; resolves the original "error fires after OTP" bug).

const SIGN_IN_PATH = '/login?next=/dashboard';

interface AccountStepProps extends PostVerifyLookupDeps {
  flow: ReturnType<typeof useSelfServiceSMBSignupFlow>;
  onSignup: (data: AccountData) => Promise<SignupOutcome>;
}

export function AccountStep({
  flow,
  domainMatch,
  pendingProjectInvite,
  onSignup,
}: AccountStepProps): JSX.Element {
  const { search } = useLocation();
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const social = useAccountStepSocial(setSubmitError);
  const { handoff } = social;
  const [mfaRequiredEmail, setMfaRequiredEmail] = useState<string | null>(null);
  const [signInRequiredMessage, setSignInRequiredMessage] = useState<string | null>(null);

  // "change" remounts the form without the line that held focus: First Name
  // takes it once the new form is in.
  const firstNameInput = useRef<HTMLInputElement>(null);
  const refocusFirstName = useRef(false);
  useEffect(() => {
    if (!refocusFirstName.current) return;
    refocusFirstName.current = false;
    firstNameInput.current?.focus();
  }, [social.formKey]);
  const handleChangeAccount = () => {
    refocusFirstName.current = true;
    social.changeAccount();
  };

  const handleSubmit = useCallback(
    async (values: SMBSignupFormData) => {
      const data: AccountData = {
        firstName: values.first_name.trim(),
        lastName: values.last_name.trim(),
        // A social sign-up's form has no email field (ENG-6311).
        email: handoff ? handoff.email : values.email.trim(),
        ...(handoff ? { social: handoff.social } : { password: values.password }),
        // companyName collected on the team-create step, not here.
        companyName: '',
        acceptTerms: !!values.accept_privacy_and_terms_of_use,
      };
      try {
        setBusy(true);
        setSubmitError(null);
        setMfaRequiredEmail(null);
        setSignInRequiredMessage(null);
        flow.clearError();
        const { emailVerified } = await onSignup(data);
        if (!emailVerified) {
          flow.accountCreated(data);
          return undefined;
        }
        const lookups = await runPostVerifyLookups(
          { domainMatch, pendingProjectInvite },
          { email: data.email, step: 'account' }
        );
        if (lookups.status === 'refused') {
          endOnSignInRequired(flow, lookups.error);
        } else if (lookups.status === 'resolved') {
          flow.accountVerified(data, lookups.pendingInvite, lookups.domainMatches);
        } else {
          // Both lookups failed. The account exists, so there is no retry
          // here: on to team-create with no matches, as the resume path
          // does, where SubscriptionStep's invite guard still applies.
          flow.accountVerified(data, null, []);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Signup failed';
        // Map API server-side errors to React Final Form field-level errors
        // so they surface inline next to the offending input (matches the
        // SMBSignupForm pattern in useSMBSignup.ts).
        const fieldErrors = accountStepFieldErrors(err);
        // A social sign-up's form has no email field: the banner says it.
        if (fieldErrors && handoff) {
          setSubmitError(fieldErrors.email);
          return undefined;
        }
        if (fieldErrors) return fieldErrors;
        // The sign-up lapsed or the account was taken meanwhile: back to a
        // password signup, saying why.
        const code = firstErrorCode(err);
        if (handoff && code && SOCIAL_SIGNUP_ENDED.has(code)) {
          social.endHandoff();
          setSubmitError(socialErrorMessage(code, handoff.social.provider) ?? message);
          return undefined;
        }
        // The signup mutation succeeded server-side but createToken bounced
        // because the account is enrolled in (or needs to enroll) MFA. The
        // user can't retry this form — their account exists. Route them to
        // the login flow which knows how to drive MFA setup.
        if (errorHasCode(err, ERROR_CODES.AUTH_MFA_ENROLLMENT_REQUIRED)) {
          setMfaRequiredEmail(data.email);
          return undefined;
        }
        // Same dead end, any other token failure (ENG-5760): the account
        // exists, so resubmitting can only hit "already exists".
        if (err instanceof SignupSessionError) {
          setSignInRequiredMessage(err.message);
          return undefined;
        }
        setSubmitError(message);
      } finally {
        setBusy(false);
      }
      return undefined;
    },
    [flow, onSignup, handoff, social, domainMatch, pendingProjectInvite]
  );

  // Carry campaign params through the login round-trip, or an MFA-enrolled
  // user (or, ENG-6166, one whose provider account names an existing account)
  // silently loses their trial on the way back (ENG-5527).
  const loginHref = `/login?next=${encodeURIComponent(
    appendSignupParams('/signup/customer', search)
  )}`;

  if (mfaRequiredEmail || signInRequiredMessage) {
    const copy =
      signInRequiredMessage ??
      getDefaultErrorMessage(ERROR_CODES.AUTH_MFA_ENROLLMENT_REQUIRED) ??
      'Your account needs multi-factor authentication set up before you can continue.';
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 2.75 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1.5, fontSize: 24 }}>
            Finish setup
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5, mb: 2 }}>
            {copy}
          </Typography>
          <Link
            component={RouterLink}
            to={loginHref}
            sx={{ fontWeight: 600, color: 'var(--brandPrimary)' }}
          >
            Sign in to continue
          </Link>
        </Box>
      </Box>
    );
  }

  // Between the fields and the terms: the buttons only start the provider
  // flow that prefills the form, and the submit button stays the one way in.
  // ENG-6311: a provider sign-up confirms in a short form, the email named here.
  const signingUpAs = handoff && (
    <p className="m-[0] mb-12 break-words text-center text-14 text-on-surface">
      <span>{`Signing up as ${handoff.email} via ${socialProviderName(handoff.social.provider)}`}</span>
      {/* No break before the dot: "· change" stays on the provider's line. */}
      <span className="whitespace-nowrap">
        <span aria-hidden="true">{'\u00a0· '}</span>
        <Button
          className="min-h-48"
          variant="tertiary"
          // The Sign up in flight carries this sign-up.
          disabled={busy}
          onClick={handleChangeAccount}
        >
          change
        </Button>
      </span>
    </p>
  );

  const socialButtons = !handoff && isSocialSignInEnabled() && (
    <div className="mb-32 mt-20">
      {/* Google's button stops at 400px; held to that, Microsoft's matches it. */}
      <div className="mx-auto w-full max-w-[400px]">
        <SocialSignInButtons
          text="continue_with"
          dividerPlacement="above"
          dividerLabel="or continue with"
          disabled={busy || social.pending}
          onCredential={(provider, idToken) => {
            setSubmitError(null);
            void social.handleCredential(provider, idToken);
          }}
          onError={social.showFailure}
        />
      </div>
      {social.confirmAccount && (
        <Alert preset="notification" role="status" className="mt-24">
          <span>{`An OnFrontiers account already uses ${social.confirmAccount.email ?? `your ${socialProviderName(social.confirmAccount.provider)} email`}. `}</span>
          <RouterLink to={loginHref} state={{ email: social.confirmAccount.email }}>
            Log in
          </RouterLink>
          <span> to continue.</span>
        </Alert>
      )}
    </div>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2.75, pl: '2px', pr: '7px' }}>
        <Typography
          id="self-service-smb-account-title"
          variant="h5"
          component="h2"
          sx={{ fontWeight: 700, mb: 1.5, fontSize: 24 }}
        >
          {STEP_COPY.account.formTitle}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
          {STEP_COPY.account.formSubtitle}
        </Typography>
      </Box>
      <SignupCredentialsForm
        key={social.formKey}
        onSubmit={handleSubmit}
        // A provider answer in flight owns the form: a password signup now
        // would race its sign-in or prefill.
        loading={busy || social.pending}
        error={submitError}
        submitLabel={STEP_COPY.account.submit}
        submittingLabel="Creating account…"
        socialPrefill={handoff}
        beforeFields={signingUpAs}
        firstNameRef={firstNameInput}
        beforeAgreements={socialButtons}
      />
      {/* Mirrors SMBSignupForm's footer. Uses the React Router Link so the
          modal context can unmount cleanly — the previous hard navigation
          existed to force Apollo + PermissionService re-init, both of
          which are now handled by authSession.authenticate(). */}
      <Box sx={{ textAlign: 'center', mt: 1, fontSize: 14 }}>
        Already have an account?{' '}
        <Box
          component={RouterLink}
          to={SIGN_IN_PATH}
          sx={{
            color: 'var(--brandPrimary)',
            textDecoration: 'none',
            '&:hover': { opacity: 0.8 },
          }}
        >
          Sign in
        </Box>
      </Box>
    </Box>
  );
}
